import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useCloud } from '../context/CloudContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import { Loading, ErrorState } from '../components/States';
import { currency } from '../lib/format';
import { toId } from '../lib/ids';

const fmtDate = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};
const dateOnly = (iso) => (iso ? String(iso).slice(0, 10) : '');

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { providers } = useCloud();
  const { data, loading, error, reload } = useAsync(() => api.getContract(id), [id]);
  const groupsReq = useAsync(() => api.getUserGroups(), []);
  const [groupId, setGroupId] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (data) setGroupId(data.groupId ? String(data.groupId) : '');
  }, [data]);

  if (loading) return <Loading label="Loading contract…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const c = data;
  const groups = groupsReq.data || [];
  const providerName =
    providers.find((p) => p.id === c.providerId)?.name || c.providerId;

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.updateContract(id, {
        providerId: c.providerId,
        type: c.type,
        start: dateOnly(c.start),
        end: dateOnly(c.end),
        value: c.value,
        currency: c.currency,
        groupId: toId(groupId),
      });
      setMsg('Association saved.');
      reload();
    } catch (e) {
      setMsg(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    await api.deleteContract(id).catch(() => {});
    navigate('/contracts');
  };

  return (
    <div className="stack">
      <PageHeader
        title={`${c.type} contract`}
        subtitle={`${providerName} · ${currency(c.value, c.currency)}`}
        actions={
          <>
            <Link className="btn btn--ghost" to="/contracts">
              Back
            </Link>
            <button className="btn btn--danger-ghost" onClick={remove}>
              Delete
            </button>
          </>
        }
      />

      <div className="grid grid--2">
        <Card title="Contract details">
          <div className="kv">
            <div>
              <span className="kv__k">Provider</span>
              <span>{providerName}</span>
            </div>
            <div>
              <span className="kv__k">Type</span>
              <span>{c.type}</span>
            </div>
            <div>
              <span className="kv__k">Start</span>
              <span>{fmtDate(c.start)}</span>
            </div>
            <div>
              <span className="kv__k">End</span>
              <span>{fmtDate(c.end)}</span>
            </div>
            <div>
              <span className="kv__k">Value</span>
              <span>{currency(c.value, c.currency)}</span>
            </div>
            <div>
              <span className="kv__k">Currency</span>
              <span>{c.currency}</span>
            </div>
            <div>
              <span className="kv__k">Contract ID</span>
              <span className="mono">{c.id}</span>
            </div>
          </div>
        </Card>

        <Card title="Associate user group">
          <p className="listview__desc">
            Assign this contract to a user group. The group's users are governed by this
            contract.
          </p>
          <label className="field">
            <span>User group</span>
            <select
              className="input"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">Unassigned</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                  {g.tenancyName ? ` · ${g.tenancyName}` : ''}
                </option>
              ))}
            </select>
          </label>
          <div className="modal__footer">
            <button className="btn btn--primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save association'}
            </button>
          </div>
          {msg ? <div className="field__hint">{msg}</div> : null}
          <p className="field__hint">
            Currently: <strong>{c.groupName || 'Unassigned'}</strong>
          </p>
        </Card>
      </div>
    </div>
  );
}
